/* eslint-disable no-console */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tenantApiPath } from "../../actions/tenantApi";
import { convert_to_comma_seperated_list } from "../../actions/util";
import { callAvosApi } from "../../utils/useAvosApiHook";

interface PatchCheckPayload {
  field: string;
  state_value: any;
  value: any;
  keep_history: boolean;
}

export const checkQueryKey = (check: any) => ["check", check?.tenant_id, check?.check_id];

// `seed` writes the entry even when the check was never fetched, which is how an
// offline check — it has no server row — gets into the cache in the first place.
function writeCheckToCaches(queryClient: any, check: any, patch: any, seed = false) {
    queryClient.setQueryData(checkQueryKey(check), (current: any) => {
        if (!current && !seed) return current;
        return { ...(current ?? {}), ...patch };
    });
    queryClient.setQueriesData({ queryKey: ["layerChecks", check?.tenant_id] }, (checks: any) => {
        return checks?.map((item: any) => (item.check_id === check?.check_id ? { ...item, ...patch } : item));
    });
}

/**
 * Mutation hook for patching a check field.
 * Derives tenant_id and check_id from the check entity.
 *
 * Usage:
 *   const mutation = usePatchCheckMutation();
 *   mutation.mutate(check, "status", "complete");
 */
export function usePatchCheckMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        // See usePatchAvocadoMutation for the networkMode: "always" rationale —
        // this PATCH is one of apiClientOfflineCapture's four recognised endpoints.
        networkMode: "always",
        onMutate: async ({ check, field, value }) => {
            await queryClient.cancelQueries({ queryKey: ["layerChecks", check.tenant_id] });
            const previous = queryClient.getQueriesData<unknown[]>({ queryKey: ["layerChecks", check.tenant_id] });
            const previousCheck = queryClient.getQueryData(checkQueryKey(check));
            writeCheckToCaches(queryClient, check, { [field]: value });
            return { previous, previousCheck };
        },
        mutationFn: async (args: {
      check: any;
      field: string;
      value: any;
      keep_history?: boolean;
    }) => {
            const { check, field, value, keep_history = false } = args;

            if (!check?.tenant_id || !check?.check_id) {
                throw new Error("Cannot patch a check without tenant_id and check_id");
            }

            const payload: PatchCheckPayload = {
                field,
                state_value: value,
                value: convert_to_comma_seperated_list(value),
                keep_history,
            };

            const url = `${tenantApiPath(check.tenant_id, "checks/")}${check.check_id}/`;

            const response = await callAvosApi(`/${url}`, { method: "PATCH", data: payload });
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({
                queryKey: ["layerChecks", variables.check.tenant_id],
            });
        },
        onError: (error, variables, context) => {
            context?.previous.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
            queryClient.setQueryData(checkQueryKey(variables.check), context?.previousCheck);
        },
    });
}

/**
 * Writes a complete check into the cache with no API call. This full-entity
 * writer is reserved for seeding synthetic offline checks.
 */
export function useUpdateCheckStateLocally() {
    const queryClient = useQueryClient();
    return (check: any) => {
        writeCheckToCaches(queryClient, check, check, true);
        return Promise.resolve();
    };
}

/**
 * Merges only the changed fields into a cached check.
 *
 * Form callbacks can update an input and one or more derived fields
 * synchronously. Accepting a partial patch prevents a later callback write from
 * restoring sibling values from the render's stale check snapshot.
 */
export function usePatchCheckStateLocally() {
    const queryClient = useQueryClient();
    return (check: any, patch: Record<string, any>) => {
        writeCheckToCaches(queryClient, check, patch);
        return Promise.resolve();
    };
}

/**
 * Appends a fruit to the cached check. Offline fruits exist only in the cache —
 * there is no server row to refetch — so the append has to happen here, and it
 * reads the cache rather than the caller's snapshot so a loop accumulates.
 */
export function useAppendFruitToCheckCache() {
    const queryClient = useQueryClient();
    return (check: any, fruit: any) => {
        queryClient.setQueryData(checkQueryKey(check), (current: any) => {
            const target = current ?? check;
            return { ...target, avocados: [...(target?.avocados ?? []), fruit] };
        });
    };
}
